import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { CHANNELS, COLUMNS, TABLES, type Platform, type ReleaseChannel } from '@/lib/db';
import { env } from '@/lib/env';
import { queryKeys } from '@/lib/queryKeys';
import { supabase } from '@/lib/supabase';

export type Release = {
  id: string | null;
  version: string;
  channel: ReleaseChannel;
  releasedAt: string;
  notesMd: string | null;
  windowsUrl: string | null;
  windowsSha256: string | null;
  macosUrl: string | null;
  macosSha256: string | null;
  isPublished: boolean;
};

type ReleaseRow = {
  id: string;
  version: string;
  channel: string;
  released_at: string;
  notes_md: string | null;
  windows_url: string | null;
  windows_sha256: string | null;
  macos_url: string | null;
  macos_sha256: string | null;
  is_published: boolean;
};

type GitHubAsset = {
  name: string;
  browser_download_url: string;
};

type GitHubRelease = {
  id: number;
  tag_name: string;
  name: string | null;
  body: string | null;
  draft: boolean;
  prerelease: boolean;
  published_at: string | null;
  created_at: string;
  assets: GitHubAsset[];
};

const WINDOWS_ASSET_RE = /\.(msi|exe)$/i;
const MACOS_ASSET_RE = /\.(dmg|pkg)$/i;

function asChannel(value: string): ReleaseChannel {
  return value === CHANNELS.beta ? CHANNELS.beta : CHANNELS.stable;
}

function rowToRelease(row: ReleaseRow): Release {
  return {
    id: row.id,
    version: row.version,
    channel: asChannel(row.channel),
    releasedAt: row.released_at,
    notesMd: row.notes_md,
    windowsUrl: row.windows_url,
    windowsSha256: row.windows_sha256,
    macosUrl: row.macos_url,
    macosSha256: row.macos_sha256,
    isPublished: row.is_published,
  };
}

function stripVPrefix(tag: string): string {
  return tag.replace(/^v/i, '');
}

function pickAsset(assets: GitHubAsset[], pattern: RegExp): GitHubAsset | undefined {
  return assets.find((a) => pattern.test(a.name));
}

function ghReleaseToRelease(r: GitHubRelease): Release {
  const win = pickAsset(r.assets, WINDOWS_ASSET_RE);
  const mac = pickAsset(r.assets, MACOS_ASSET_RE);
  return {
    id: null,
    version: stripVPrefix(r.tag_name),
    channel: r.prerelease ? CHANNELS.beta : CHANNELS.stable,
    releasedAt: r.published_at ?? r.created_at,
    notesMd: r.body,
    windowsUrl: win?.browser_download_url ?? null,
    windowsSha256: null,
    macosUrl: mac?.browser_download_url ?? null,
    macosSha256: null,
    isPublished: !r.draft,
  };
}

async function fetchReleasesFromSupabase(): Promise<Release[] | null> {
  const { data, error } = await supabase
    .from(TABLES.releases)
    .select('*')
    .eq(COLUMNS.releases.isPublished, true)
    .order(COLUMNS.releases.releasedAt, { ascending: false });
  if (error) return null;
  const rows = (data ?? []) as ReleaseRow[];
  if (rows.length === 0) return null;
  return rows.map(rowToRelease);
}

async function fetchReleasesFromGitHub(): Promise<Release[]> {
  const url = `https://api.github.com/repos/${env.VITE_GITHUB_REPO}/releases?per_page=30`;
  const res = await fetch(url, {
    headers: { Accept: 'application/vnd.github+json' },
  });
  if (!res.ok) {
    throw new Error(`GitHub releases fetch failed: ${res.status} ${res.statusText}`);
  }
  const list = (await res.json()) as GitHubRelease[];
  return list
    .filter((r) => !r.draft)
    .map(ghReleaseToRelease)
    .sort((a, b) => b.releasedAt.localeCompare(a.releasedAt));
}

export async function fetchReleases(): Promise<Release[]> {
  const fromDb = await fetchReleasesFromSupabase();
  if (fromDb && fromDb.length > 0) return fromDb;
  return fetchReleasesFromGitHub();
}

export async function fetchLatestRelease(
  channel: ReleaseChannel = CHANNELS.stable,
): Promise<Release | null> {
  const all = await fetchReleases();
  return all.find((r) => r.channel === channel) ?? null;
}

export function useReleases(): UseQueryResult<Release[], Error> {
  return useQuery({
    queryKey: queryKeys.releases(),
    queryFn: fetchReleases,
  });
}

export function useLatestRelease(
  channel: ReleaseChannel = CHANNELS.stable,
): UseQueryResult<Release | null, Error> {
  return useQuery({
    queryKey: queryKeys.latestRelease(channel),
    queryFn: () => fetchLatestRelease(channel),
  });
}

export function getDownloadUrl(release: Release, platform: Platform): string | null {
  return platform === 'windows' ? release.windowsUrl : release.macosUrl;
}

export function getSha256(release: Release, platform: Platform): string | null {
  return platform === 'windows' ? release.windowsSha256 : release.macosSha256;
}
